import React, { useEffect, useState } from "react"
import { formatDateDataGrid } from "src/utils/format-date"
import PropTypes from 'prop-types'
import DatalakeService from "src/services/datalake-service"
import ParameterPaginationTable from "src/components/table/parameter-pagination-table"
import { renderCellDate } from "src/utils/table-render-functions"

const ParameterDatalakeCVMDRETable = (props) => {
    const dataLakeService = new DatalakeService()
    const [data, setData] = useState([])
    const [pagination, setPagination] = useState({ count: 0, next: null, previous: null })

    useEffect(() => {
        loadData()
    }, [])

    const loadData = () => {
        props.loading(true)
        dataLakeService.loadPaginationEndpoint(props.endpoint).then((response) => {
            props.loading(false)

            const { body } = response
            const { results, count, next, previous } = body
            const list = results.map((obj) => ({ ...obj, id: obj.ID}))    
            setPagination({ count: count, next: next, previous: previous })
            setData(list)
        })
    }

    const createRow = async () => {
        const body = {
            "ID_DF": null,
            "CPFCNPJ": null,
            "DataReferencia": null,
            "Versao": null,
            "CodigoCVM": null,
            "GrupoDFP": null,
            "Moeda": null,
            "EscalaMoeda": null,
            "OrdemExercicio": null,
            "DataInicioExercicio": null,
            "DataFimExercicio": null,
            "CodigoConta": null,
            "DescricaoConta": null,
            "SaldoConta": null,
            "SituacaoFixaConta": null,
            "NivelConta": null,
            "BaseBalanco": null,
            "GrupoBalanco": null,
            "Ano": null
        }

        props.loading(true)
        return dataLakeService.createTableEndpoint(props.endpoint, body).then(() => {
            props.loading(false)
            loadData(0, 25)
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }

    const updateRow = async (newRow) => {
        const { ID, ID_DF, CPFCNPJ, DataReferencia, Versao, CodigoCVM, GrupoDFP, Moeda, EscalaMoeda,
        OrdemExercicio, DataInicioExercicio, DataFimExercicio, CodigoConta, DescricaoConta,
        SaldoConta, SituacaoFixaConta, NivelConta, BaseBalanco, GrupoBalanco, Ano
            } = newRow

        const body = {
            "ID_DF": ID_DF,
            "CPFCNPJ": CPFCNPJ,
            "DataReferencia": DataReferencia,
            "Versao": Versao,
            "CodigoCVM": CodigoCVM,
            "GrupoDFP": GrupoDFP,
            "Moeda": Moeda,
            "EscalaMoeda": EscalaMoeda,
            "OrdemExercicio": OrdemExercicio,
            "DataInicioExercicio": DataInicioExercicio,
            "DataFimExercicio": DataFimExercicio,
            "CodigoConta": CodigoConta,
            "DescricaoConta": DescricaoConta,
            "SaldoConta": SaldoConta,
            "SituacaoFixaConta": SituacaoFixaConta,
            "NivelConta": NivelConta,
            "BaseBalanco": BaseBalanco,
            "GrupoBalanco": GrupoBalanco,
            "Ano": Ano
        }

        props.loading(true)
        return dataLakeService.updateTableEndpoint(props.endpoint, ID, body).then(() => {
            props.loading(false)
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }

    const deleteRow = async (row) => {
        props.loading(true)
        const { ID } = row
        return dataLakeService.deleteTableEndpoint(props.endpoint, ID).then(() => {
            props.loading(false)
            const listRemoved = data.filter((item) => item.ID !== ID)
            setData(listRemoved)
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }

    const onFilter = async (filterList, limit, offset) => {
        const query = filterList.map(obj => `${encodeURIComponent(obj.field)}=${encodeURIComponent(obj.value)}`).join('&')

        props.loading(true)
        dataLakeService.loadPaginationEndpoint(props.endpoint, query, limit, offset).then((response) => {
            props.loading(false)
            const { body } = response
            const { results, count, next, previous } = body
            const list = results.map((obj, index) => ({ ...obj, id: index}))
            setPagination({ count: count, next: next, previous: previous })
            setData(list)
        })
    }

    const changePage = async (endpoint) => {
        props.loading(true)
        dataLakeService.paginate(endpoint).then((response) => {
            props.loading(false)
            const { body } = response
            const { results, count, previous, next } = body
            const list = results.map((obj, index) => ({ ...obj, id: index}))

            setPagination({ count: count, next: next, previous: previous })
            setData([])
            setData(list)
        })
    }

    const columns = [
        {
            field: 'ID',
            headerName: '#',
            disableColumnMenu: true,
            flex: true,
            minWidth: 80,
        },
        {
            field: 'ID_DF',
            headerName: 'ID_DF',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150,
        },
        {
            field: 'CPFCNPJ',
            headerName: 'CPFCNPJ',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150,
        },
        {
            field: 'DataReferencia',
            headerName: 'DataReferencia',
            typeComponent: 'date',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150,
            renderCell: (params) => renderCellDate(params)
        },
        {
            field: 'Versao',
            headerName: 'Versao',
            type: 'number',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150,
        },
        {
            field: 'CodigoCVM',
            headerName: 'CodigoCVM',
            type: 'number',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150,
        },
        {
            field: 'GrupoDFP',
            headerName: 'GrupoDFP',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150,
        },
        {
            field: 'Moeda',
            headerName: 'Moeda',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150,
        },
        {
            field: 'EscalaMoeda',
            headerName: 'EscalaMoeda',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150,
        },
        {
            field: 'OrdemExercicio',
            headerName: 'OrdemExercicio',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150,
        },
        {
            field: 'DataInicioExercicio',
            headerName: 'DataInicioExercicio',
            typeComponent: 'date',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150,
            renderCell: (params) => renderCellDate(params)
        },
        {
            field: 'DataFimExercicio',
            headerName: 'DataFimExercicio',
            typeComponent: 'date',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150,
            renderCell: (params) => renderCellDate(params)
        },
        {
            field: 'CodigoConta',
            headerName: 'CodigoConta',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150,
        },
        {
            field: 'DescricaoConta',
            headerName: 'DescricaoConta',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150,
        },
        {
            field: 'SaldoConta',
            headerName: 'SaldoConta',
            type: 'number',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150,
        },
        {
            field: 'SituacaoFixaConta',
            headerName: 'SituacaoFixaConta',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150,
        },
        {
            field: 'NivelConta',
            headerName: 'NivelConta',
            type: 'number',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150,
        },
        {
            field: 'BaseBalanco',
            headerName: 'BaseBalanco',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150,
        },
        {
            field: 'GrupoBalanco',
            headerName: 'GrupoBalanco',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150,
        },
        {
            field: 'Ano',
            headerName: 'Ano',
            type: 'number',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150,
        },
        {
            field: 'CriadoEm',
            headerName: 'Data de Criação',
            flex: true,
            minWidth: 150,
            disableColumnMenu: true,
            renderCell: (params) => formatDateDataGrid(params)
        },
        {
            field: 'AtualizadoEm',
            headerName: 'Data de Atualização',
            flex: true,
            minWidth: 150,
            disableColumnMenu: true,
            disableFilter: true,
            renderCell: (params) => formatDateDataGrid(params)
        }
    ]

    return (
        <div>
            <ParameterPaginationTable
                columns={columns}
                data={data}
                setData={setData}
                createRow={createRow}
                deleteRow={deleteRow}
                updateRow={updateRow}
                filterFunction={onFilter}
                pagination={pagination}
                onChangePage={changePage}
                customFilter={true} />
        </div>
    )
}

ParameterDatalakeCVMDRETable.propTypes = {
    loading: PropTypes.func,
    endpoint: PropTypes.string,
}

export default ParameterDatalakeCVMDRETable